// Lib
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';

// Components
import Layout from '../../components/Layout';
import Products from '../../components/Products';
import FiltersBar from '../../components/FiltersBar';
import Spinner from '../../components/Spinner';
import TotalQtyLabel from '../../components/Labels/TotalQtyLabel';
import Error from '../../components/Error';

// Hooks
import useFilters from './hooks/useFilters';
import useCatalogView from './hooks/useCatalogView';

// Actions
import { GetProducts } from '../../actions/products';

// Styles
import { useStyles } from './MaterialsStyles';

const Materials = ({ children, onlyContent = false }) => {
  const dispatch = useDispatch();
  const apiError = useSelector((state) => state.apiError?.['getProducts']);
  const { classes } = useStyles({ onlyContent });
  const {
    products,
    updateShownProducts,
    onChangeFilter,
    onMultipleSelectChange,
    applyMobileFilters,
    onRemoveFilter,
    filters,
    onClearAllFilters,
    isLoading,
    onChangeSorting,
    getDependentFilters,
    dependentFilters,
  } = useFilters();
  const { catalogView } = useCatalogView();

  useEffect(() => {
    dispatch(GetProducts());
  }, [dispatch]);

  const renderComponent = useCallback(
    () => (
      <FiltersBar
        onlyContent={onlyContent}
        onChangeFilter={onChangeFilter}
        onMultipleSelectChange={onMultipleSelectChange}
        applyMobileFilters={applyMobileFilters}
        onRemoveFilter={onRemoveFilter}
        filters={filters}
        onClearAllFilters={onClearAllFilters}
        onChangeSorting={onChangeSorting}
        dependentFilters={dependentFilters}
        getDependentFilters={getDependentFilters}
      />
    ),
    [
      onlyContent,
      onChangeFilter,
      onMultipleSelectChange,
      applyMobileFilters,
      onRemoveFilter,
      filters,
      onClearAllFilters,
      onChangeSorting,
      getDependentFilters,
      dependentFilters,
    ],
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <Spinner />;
    } else if (apiError) {
      return <Error message={apiError} />;
    } else if (products?.all?.ids && products?.all?.ids?.length === 0) {
      return (
        <Typography className={classes.emptyMaterials} variant="h1">
          There are currently no products in your product catalog
        </Typography>
      );
    } else {
      return (
        <>
          {products?.all?.ids && (
            <TotalQtyLabel
              qty={products?.afterFilters?.length}
              type="product"
              className={classes.productsQtyLabel}
            />
          )}
          <Products
            updateShownProducts={updateShownProducts}
            products={products}
            view={catalogView}
          />
          {children}
        </>
      );
    }
  }, [
    apiError,
    children,
    products,
    isLoading,
    catalogView,
    classes,
    updateShownProducts,
  ]);

  return (
    <Layout
      title="Materials"
      onlyContent={onlyContent}
      className={{ layout: classes.layout }}
      renderComponent={renderComponent}>
      {content}
    </Layout>
  );
};

export default Materials;
